<template>
    <widget-card
        colClasses="xs12"
        :heading="widget.text.caption[$i18n.locale]" 
        custom-card-classes="chart-wrapper">
        <bar-chart-contractual 
            class="chart-wrapper" 
            :sets="series" 
            :is-money-value="isMoneyValue" 
            :styles="myStyles">
        </bar-chart-contractual>
    </widget-card>
</template>

<script>
import Enumerable from 'linq'
import BarChartContractual from './BarChartContractual.js'
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'

export default {
    props: {
        widget: {
            type: Object,
            required: true
        }
    },
    components: {
        BarChartContractual
    },
    computed: {
        ...mapGetters([
            'season',
            'reportingData',
            'lastSeasonReportingData',
            'selectedF1Event',
            'contractualContingent',
            'contractualContingentByEvent'
        ]),
        myStyles () {
            return {
                height: `95%`,
                position: 'relative'
            }
        },
        series() {
            // Filter the relevant data based on the selected event and partner (not agencies)
            const filteredData = this.reportingData.filter(item => {
                // Check partner conditions:
                // - ShopName must NOT contain "Agency"
                // - wo_partner_name MUST be filled
                // - is_contractual must be 1
                if (!item.wo_partner_name || (item.ShopName && item.ShopName.includes('Agency'))) return false;
                // if (!item.wo_partner_name || (item.ShopName && item.ShopName.includes('Agency')) || item.is_contractual !== 1) return false;
                
                // Check the selected event
                if (this.selectedF1Event === 'all') return true;
                return item.event_group_id === parseInt(this.selectedF1Event);
            }).filter(item => 
                item.FK_ArticleType === 1 || item.FK_ArticleType === 2
            );

            // Group by partners
            let enumerable = Enumerable.from(filteredData);
            
            // Calculate sold tickets per partner
            let usedData = enumerable
                .groupBy(
                    item => item.wo_partner_name,
                    null,
                    (key, g) => ({
                        label: key,
                        fromDate: g.first().wo_date,
                        value: g.sum(x => parseInt(x.Menge) || 0),
                        color: getPartnerColor(key, 'used')
                    })
                )
                .orderBy(x => x.label)
                .toArray();


            // Get all unique partner names
let contingentMap = {};

if (this.selectedF1Event === 'all') {
    // Für alle Events
    contingentMap = {...this.contractualContingent};
} else {
    // Für ein spezifisches Event
    // Vorfiltern der Kontingente nach dem ausgewählten Event
    const filteredContingents = this.contractualContingentByEvent[this.selectedF1Event] || [];
    // console.log("Gefilterte Kontingente für Event", this.selectedF1Event, ":", filteredContingents);
    
    // Map der Kontingente erstellen
    filteredContingents.forEach(c => {
        // Sicherstellen, dass partner_name existiert
        if (c.partner_name) {
            contingentMap[c.partner_name] = c;
        }
    });
    
    // Überprüfen, ob Kontingente gefunden wurden
    if (Object.keys(contingentMap).length === 0) {
        // console.warn("Keine Kontingente für das ausgewählte Event gefunden:", this.selectedF1Event);
        // console.log("Verfügbare Kontingente:", this.contractualContingentByEvent);
    }
}

// console.log("Debug - Vorfiltrierte Kontingente:", contingentMap, "Anzahl:", Object.keys(contingentMap).length);

// Alle Partner aus usedData (die tatsächlich gebucht haben)
const usedPartners = [...new Set(usedData.map(d => d.label))];

// Debug-Logging für jedes Element in usedData
// usedData.forEach(item => {
//     console.log(`Used Partner: ${item.label}, Value: ${item.value}`);
// });

// totalData erstellen (nur für Partner, die in usedData vorhanden sind)
let totalData = usedPartners.map(partnerName => {
    // Debug-Logging - Welches Kontingent wird für diesen Partner verwendet
    // console.log(`Verarbeite Partner: ${partnerName}`);
    // console.log(`Kontingent aus Map:`, contingentMap[partnerName]);

    const usedEntry = usedData.find(d => d.label === partnerName);
    
    let contingentValue = 0;
    if (this.selectedF1Event === 'all') {
        contingentValue = contingentMap[partnerName] ? contingentMap[partnerName].contractualTickets : 0;
    } else {
        contingentValue = contingentMap[partnerName] ? contingentMap[partnerName].ContingentContractual : 0;
    }
    
    // console.log(`Berechneter Wert für ${partnerName}: ${contingentValue}`);
    
    return {
        label: partnerName,
        fromDate: usedEntry ? usedEntry.fromDate : null,
        value: contingentValue,
        color: getPartnerColor(partnerName, 'total')
    };
});


            return [
                {
                    name: 'Total Contingent',
                    data: totalData,
                },
                {
                    name: 'Used Contingent',
                    data: usedData,
                }
            ];
        },
        isMoneyValue: function () {
            return this.widget.data.sumField ? this.widget.data.sumField.isMoneyValue : false
        }
    },
    data() {
        return {
            contractualContingentData: []
        };
    },
    created() {
        this.loadContractualContingent();
        this.loadContractualContingentByEvent();
    },
    methods: {
        ...mapActions([
            'loadContractualContingent',
            'loadContractualContingentByEvent'
        ])
    }
}

// Hilfsfunktion für Partner-spezifische Farben
function getPartnerColor(partnerName, type) {
    const colorMap = {
        'Microsoft': { used: '#4CAF50', total: '#A5D6A7' },
        'Mobilize Financial Services': { used: '#2196F3', total: '#90CAF9' },
        'Alpine Eyewear': { used: '#FF9800', total: '#FFE0B2' },
        'Sprinklr': { used: '#F44336', total: '#FFCDD2' },
        'Shamir': { used: '#009688', total: '#B2DFDB' },
        'BRB': { used: '#795548', total: '#D7CCC8' },
        'Binance': { used: '#607D8B', total: '#CFD8DC' },
        'Business Solver': { used: '#E91E63', total: '#F8BBD0' },
        'Infinox': { used: '#673AB7', total: '#D1C4E9' },
        'Unknown': { used: '#9E9E9E', total: '#EEEEEE' }
    };

    return (colorMap[partnerName] && colorMap[partnerName][type]) || colorMap['Unknown'][type];
}
</script>

<style>
.chart-wrapper {
    position: relative;
    height: 95%;
    width: 100%;
}
</style>